import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, firstValueFrom, Observable } from 'rxjs';
import { ErrorFrontend } from '@core/errors';
import {
  BoardDetail,
  BoardRow,
  DispensationRow,
  DocumentReview,
  PaginatedResponse,
  PaginationParams,
} from '@core/models';

/**
 * The board's query params: the filter bar's, plus pagination and `ordering`
 * (`-date` | `-value`). Extra keys are forwarded as-is.
 */
export interface BoardListParams extends PaginationParams {
  ordering?: string;
  search?: string;
  medication?: string;
  date_from?: string;
  date_to?: string;
  organization?: string;
  contract?: string;
  branch?: string;
  category?: string;
  status?: string;
  dispensed_by?: string;
  documents?: string;
  origin?: string;
  audited?: string;
  glosa?: string;
  leak?: string;
  [key: string]: string | number | boolean | undefined;
}

/**
 * The Dispensaciones board (`/api/audits/board/`): one row per pre-dispensation,
 * with its dispensation and its audit. Takes the filter bar's params, plus
 * `ordering` (`-date` | `-value`).
 */
@Injectable({ providedIn: 'root' })
export class BoardService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = 'audits/board';

  private params(params?: BoardListParams): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(params ?? {})) {
      if (value !== undefined && value !== null && value !== '') out[key] = String(value);
    }
    return out;
  }

  private request<T>(path: string, params?: BoardListParams, base = this.baseUrl): Promise<T> {
    return firstValueFrom(
      this.http.get<T>(`${base}/${path}`, { params: this.params(params) }).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  list(params?: BoardListParams): Promise<PaginatedResponse<BoardRow>> {
    return this.request('', params);
  }

  get(id: number): Promise<BoardDetail> {
    return this.request(`${id}/`);
  }

  // ── Dispensaciones: one row per ERP dispensation (rediseño de datos C) ──

  dispensations(params?: BoardListParams): Promise<PaginatedResponse<DispensationRow>> {
    return this.request('', params, 'audits/dispensations');
  }

  dispensation(id: number): Promise<BoardDetail> {
    return this.request(`${id}/`, undefined, 'audits/dispensations');
  }

  /** Who has dispensed, within the user's scope: the "Dispensó" options. */
  dispensers(): Promise<{ value: number; label: string }[]> {
    return this.request('dispensers/');
  }

  // ── Documents (fase 1b): the dispensation's, apart from its audit ─────────

  private send<T>(call: Observable<T>): Promise<T> {
    return firstValueFrom(
      call.pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  documents(dispensationId: number): Promise<DocumentReview[]> {
    return this.send(
      this.http.get<DocumentReview[]>('audits/documents/', {
        params: { dispensation: String(dispensationId) },
      }),
    );
  }

  /** What the dispensation's contract requires, reviewed yet or not. */
  async requiredDocuments(dispensationId: number): Promise<string[]> {
    const body = await this.send(
      this.http.get<{ required: string[] }>('audits/documents/required/', {
        params: { dispensation: String(dispensationId) },
      }),
    );
    return body.required;
  }

  /** Subsanar: the file goes where the document lives (the ERP, or CP Scan). */
  remedy(reviewId: number, file: File): Promise<DocumentReview> {
    const body = new FormData();
    body.append('file', file);
    return this.send(this.http.post<DocumentReview>(`audits/documents/${reviewId}/remedy/`, body));
  }

  retryRemedy(reviewId: number): Promise<DocumentReview> {
    return this.send(
      this.http.post<DocumentReview>(`audits/documents/${reviewId}/retry_remedy/`, {}),
    );
  }

  /** The document itself — Aleia's copy, or read from CP Scan — as a blob: the
   *  endpoint needs the session, so a plain link could not open it. */
  documentFile(reviewId: number): Promise<Blob> {
    return this.send(this.http.get(`audits/documents/${reviewId}/file/`, { responseType: 'blob' }));
  }

  /** The PDF an EPS portal issued with an authorization, as a blob (it needs the session). */
  authorizationFile(authorizationId: number): Promise<Blob> {
    return this.send(
      this.http.get(`dispensations/authorizations/${authorizationId}/file/`, {
        responseType: 'blob',
      }),
    );
  }

  /** "Re-verificar": read every required document of the dispensation now. */
  recheck(dispensationId: number): Promise<void> {
    return this.send(
      this.http.post<void>('audits/documents/review/', { dispensation: dispensationId }),
    );
  }
}
